/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import GetRequestsForPaymentListItem from "@/models/api/finances/GetRequestsForPaymentListItem";
import { DataOptions } from "vuetify/types";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { FILTER_NAMES } from "@/static/filterNames";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import GenerateOverdueDocumentRequest from "@/models/api/finances/GenerateOverdueDocumentRequest";
import GetContractorForRequestForPaymentResponse from "@/models/api/finances/GetContractorForRequestForPaymentResponse";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { REQUEST_FOR_PAYMENT_DOCUMENT_TYPES } from "@/static/requestForPaymentDocumentTypes";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterFormatDate } from "@/filters/formatDate";
import {
  getContractorForRequestForPayment,
  getOverduePaymentsDocumentsTable,
  postGenerateOverdueDocument,
  postRemoveDocument,
  showRequestForPayment,
} from "@/requests/finances/requestsRequestsForPayments";
import { MESSAGE_TYPES } from "@/static/messageTypes";

export interface FinancesRequestsForPaymentStateInterface {
  isLoadingRequestsForPayment: boolean;
  requestsForPayment: Array<GetRequestsForPaymentListItem>;
  requestForPaymentDataTableOptions: DataOptions;
  countRequestsForPayment: number;
  filters: null | Array<CriterionItemRequest>;
  contractor: null | GetContractorForRequestForPaymentResponse;
  selectedBankAccountId: null | number;
  selectedDocumentType: REQUEST_FOR_PAYMENT_DOCUMENT_TYPES;
}

export const FinancesRequestsForPaymentState: FinancesRequestsForPaymentStateInterface = {
  isLoadingRequestsForPayment: false,
  requestsForPayment: [],
  requestForPaymentDataTableOptions: new DefaultDataOptions(),
  countRequestsForPayment: -1,
  filters: null,
  contractor: null,
  selectedBankAccountId: null,
  selectedDocumentType: REQUEST_FOR_PAYMENT_DOCUMENT_TYPES.requestsForPayment,
};

const FinancesRequestsForPaymentModule = defineModule({
  state: FinancesRequestsForPaymentState as FinancesRequestsForPaymentStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    startRequestsForPaymentLoading(state) {
      state.isLoadingRequestsForPayment = true;
    },
    setRequestsForPayment(state, param: Array<GetRequestsForPaymentListItem>) {
      state.isLoadingRequestsForPayment = false;
      state.requestsForPayment = param;
    },
    setRequestForPaymentDataTableOptions(state, param: DataOptions) {
      state.requestForPaymentDataTableOptions = JSON.parse(JSON.stringify(param));
    },
    setRequestsForPaymentCount(state, param: number) {
      state.countRequestsForPayment = param;
    },
    setFilters(state, param: Array<CriterionItemRequest>) {
      state.filters = param;
    },
    setContractor(state, param: GetContractorForRequestForPaymentResponse | null) {
      state.contractor = param;
    },
    setSelectedBankAccountId(state, param: null | number) {
      state.selectedBankAccountId = param;
    },
    setSelectedDocumentType(state, param: REQUEST_FOR_PAYMENT_DOCUMENT_TYPES) {
      state.selectedDocumentType = param;
    },
  },
  actions: {
    async getRequestsForPaymentsTable(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.startRequestsForPaymentLoading();
      const response = await getOverduePaymentsDocumentsTable({
        count: state.requestForPaymentDataTableOptions.itemsPerPage,
        page: state.requestForPaymentDataTableOptions.page - 1,
        criterions: state.filters,
        filterName: FILTER_NAMES.BankStatementItemStandardFilter,
      });
      commit.setRequestsForPayment(response.items);
      commit.setRequestsForPaymentCount(response.totalCount);
    },
    async getContractorForRequestForPayment(context, contractorId: string) {
      const { commit, rootDispatch } = _moduleActionContext(context);
      const response = await getContractorForRequestForPayment(contractorId);
      commit.setContractor(response);
      if (response.bankAccount.defaultBankAccount !== null)
        commit.setSelectedBankAccountId(response.bankAccount.defaultBankAccount.id);
      else commit.setSelectedBankAccountId(null);

      if (!response.hasOverdueDocument) {
        // noinspection ES6MissingAwait
        rootDispatch.popup.addAlert({
          content: filterCapitalize(i18n.t("contractorHasNotOverdue", { contractorName: response.contractorName })),
          title: filterCapitalize(i18n.t("info")),
          type: MESSAGE_TYPES.Warning,
          referenceNumber: null,
        });
      }
    },
    async generateOverdueDocument(context) {
      const { dispatch, rootDispatch, state } = _moduleActionContext(context);
      const request = new GenerateOverdueDocumentRequest();
      request.documentType = state.selectedDocumentType;
      await rootDispatch.failIfRequestsInProgress();
      if (state.contractor !== null && state.selectedBankAccountId !== null) {
        request.bankAccountId = state.selectedBankAccountId;
        request.contractorId = state.contractor.contractorId;
        request.maturity = state.contractor.paymentDaysCount;
        const documentId = await postGenerateOverdueDocument(request);
        // noinspection ES6MissingAwait
        dispatch.showDocumentRequestForPayment(documentId);
        rootDispatch.popup.addSavedOkSnackbar();
        // noinspection ES6MissingAwait
        dispatch.getRequestsForPaymentsTable();
      }
    },
    async showDocumentRequestForPayment(context, documentId: string) {
      await showRequestForPayment(documentId);
    },
    async removeDocument(context, document: GetRequestsForPaymentListItem) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      const documentName = document.documentNumber;
      const date = filterFormatDate(document.issueDate);
      const contractorName = document.contractor;
      const message = filterCapitalize(
        i18n.t("doYouReallyWantToRemoveDocument", {
          documentName: documentName,
          date: date,
          contractorName: contractorName,
        })
      );
      const confirmModel = new ConfirmModel(message, filterCapitalize(i18n.t("warning")));
      if (await rootDispatch.popup.addConfirm(confirmModel)) {
        await postRemoveDocument(document.id);
        // noinspection ES6MissingAwait
        dispatch.getRequestsForPaymentsTable();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
  },
});

export default FinancesRequestsForPaymentModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, FinancesRequestsForPaymentModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, FinancesRequestsForPaymentModule);
